import {
  afterRenderEffect,
  Directive,
  ElementRef,
  inject,
  input,
  OnDestroy,
  output,
} from "@angular/core";
import { DOCUMENT } from "@angular/common";

/** Controlled native modal: the browser contains focus, makes the background inert and restores the opener. */
@Directive({
  selector: "dialog[appModalOpen]",
  standalone: true,
  host: {
    "(cancel)": "onCancel($event)",
    "(close)": "onClose()",
    "(click)": "onBackdrop($event)",
  },
})
export class ModalOpenDirective implements OnDestroy {
  readonly appModalOpen = input.required<boolean>();
  readonly modalClosed = output<void>();
  private readonly dialog = inject<ElementRef<HTMLDialogElement>>(ElementRef);
  private readonly document = inject(DOCUMENT);
  private opener: HTMLElement | null = null;

  constructor() {
    afterRenderEffect(() => {
      const element = this.dialog.nativeElement;
      if (this.appModalOpen() && !element.open) {
        this.opener =
          this.document.activeElement instanceof HTMLElement
            ? this.document.activeElement
            : null;
        element.showModal();
      } else if (!this.appModalOpen() && element.open) {
        this.closeModal();
      }
    });
  }

  protected onCancel(event: Event): void {
    event.preventDefault();
    this.modalClosed.emit();
  }

  protected onClose(): void {
    // A queued close event must not dismiss a dialog that has already reopened.
    if (!this.dialog.nativeElement.open) {
      this.restoreOpener();
      this.modalClosed.emit();
    }
  }

  protected onBackdrop(event: MouseEvent): void {
    if (event.target === event.currentTarget) {
      this.modalClosed.emit();
    }
  }

  ngOnDestroy(): void {
    this.closeModal();
  }

  private closeModal(): void {
    if (this.dialog.nativeElement.open) {
      this.dialog.nativeElement.close();
    }
    this.restoreOpener();
  }

  private restoreOpener(): void {
    if (this.opener?.isConnected) {
      this.opener.focus();
    }
    this.opener = null;
  }
}
